import { AppThunk } from '../storeTypes';
import {
  adminActionErrorMessage,
  archiveUser,
  changeUserEmail,
  changeUserRole,
  createUser,
  fetchUser,
  fetchUsers,
  resendUserInvite,
  resetUserPassword,
  unarchiveUser,
  updateUser,
} from '../../services/usersService';
import { CaseDto, UserDto, UserInfoDto, UserRole } from '../../types/backendType';
import {
  toClinicianInfoDto,
  toClinicianUpdateDto,
  toPatientInfoDto,
} from '../../types/mappers/userMapper';
import { setUsersAction } from '../slices/usersSlice';
import { markStatusFunctionCreator, setMainUseMetadataAction } from '../slices/appSlice';
import { skipRequest } from '../utils/requestStatus';
import { getRequestStatusSelector, getUserIdSelector } from '../selectors/appSelectors';
import { PatientInput } from '../../components/forms/create-user-form/create-patient-form/patientFormTypes';
import { notify } from '../slices/notificationsSlice';
import { UserId } from '../../models/User';
import { getUserDtosSelector } from '../selectors/usersSelectors';
import { fetchCases } from '../../services/casesService';
import { setCasesAction } from '../slices/casesSlice';
import {
  ClinicianInput,
  UserTypes,
} from '../../components/forms/create-user-form/CreateUserUtils';

export function fulfillUserAction(userId: UserId): AppThunk {
  return async (dispatch, getState) => {
    const requestStatus = getRequestStatusSelector('user')(getState());
    const users = getUserDtosSelector(getState());
    if (skipRequest({ id: userId, requestStatus }) || users[userId]) {
      return;
    }
    const markStatus = markStatusFunctionCreator(dispatch, userId, 'user');
    markStatus('inProcess');
    let userDto: UserDto | undefined = undefined;
    let caseDtos: CaseDto[] | undefined = undefined;
    try {
      userDto = await fetchUser(userId);
      const role = userDto?.user_info.role;
      if (role === 'patient') {
        caseDtos = await fetchCases(userId);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      return;
    }
    if (userDto) {
      dispatch(setUsersAction({ users: [userDto] }));
    }
    if (caseDtos) {
      dispatch(setCasesAction({ patientId: userId, cases: caseDtos }));
    }
    markStatus('succeeded');
  };
}

export function prepareMainUserAction(userId: UserId, role: UserRole): AppThunk {
  return async (dispatch) => {
    dispatch(setMainUseMetadataAction({ userId, role }));
    dispatch(fulfillUserAction(userId));
  };
}

export function fetchUsersAction(force?: boolean): AppThunk {
  return async (dispatch, getState) => {
    const requestStatus = getRequestStatusSelector('fetchUsers')(getState());
    if (!force && skipRequest({ requestStatus, id: 'fetchUsers' })) {
      return;
    }
    const markStatus = markStatusFunctionCreator(dispatch, 'fetchUsers', 'fetchUsers');
    markStatus('inProcess');
    let users: UserDto[] | undefined = undefined;
    try {
      users = await fetchUsers({});
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      return;
    }
    markStatus('succeeded');
    if (!users) {
      return;
    }
    dispatch(setUsersAction({ users }));
  };
}

// Messages name what was actually created or updated: the same thunks serve both forms.
const userLabel = (type: UserTypes) => (type === UserTypes.Patient ? 'patient' : 'clinician');
const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Duplicate-email guidance shown when an entered email already belongs to
// another account (commonly an existing clinician). Points the clinician at a
// "+1" sub-address so they can reuse the email instead of hitting an opaque
// failure.
const emailExistsMsg =
  'This email already exists. Please enter another email. ' +
  'To reuse this address, add a +1 such as Name+1@domain.com.';

export function createUserAction(
  type: UserTypes,
  userInput: PatientInput | ClinicianInput,
  emrPatientId?: string | undefined,
): AppThunk<Promise<UserDto | undefined>> {
  return async (dispatch, getState) => {
    const requestStatus = getRequestStatusSelector('createNewUser')(getState());
    if (skipRequest({ requestStatus, id: userInput.id })) {
      return;
    }
    const markStatus = markStatusFunctionCreator(dispatch, userInput.id, 'createNewUser');
    markStatus('inProcess');
    let userDto: UserDto | undefined = undefined;
    try {
      const userInfo: UserInfoDto =
        type === UserTypes.Patient && 'height' in userInput
          ? toPatientInfoDto(userInput, emrPatientId)
          : toClinicianInfoDto(userInput);
      const assignorUserId = getUserIdSelector(getState());
      if (!assignorUserId) {
        throw new Error('user is missing');
      }
      userDto = await createUser({ userInfo, assignorUserId });
      if (!userDto) {
        throw new Error();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      if (e.message.includes('email already exists')) {
        throw new Error(emailExistsMsg);
      } else {
        dispatch(
          notify({
            message: `We are having problems creating the new ${userLabel(type)}`,
            severity: 'error',
          }),
        );
      }
      return;
    }
    markStatus('succeeded');
    // Put the persisted user (with its backend-assigned user_id) into the
    // store so subsequent selectors are correct, and return it so callers
    // (e.g. the Add Patient form's post-save EMR case import) can use the
    // real id — the client-side formData.id is never sent to the backend.
    dispatch(setUsersAction({ users: [userDto] }));
    const name = `${userInput.firstName} ${userInput.lastName}`;
    dispatch(
      notify({
        message: `${capitalized(userLabel(type))} '${name}' was created successfully`,
        severity: 'success',
      }),
    );
    return userDto;
  };
}

export function updateUserAction(
  type: UserTypes,
  userInput: PatientInput | ClinicianInput,
  userId: string,
  emrPatientId?: string | undefined,
): AppThunk {
  return async (dispatch, getState) => {
    const markStatus = markStatusFunctionCreator(dispatch, userInput.id, 'updateUser');
    markStatus('inProcess');
    let userDto: UserDto | undefined = undefined;
    try {
      const updateUserFormDto =
        type === UserTypes.Patient && 'height' in userInput
          ? toPatientInfoDto(userInput, emrPatientId)
          : toClinicianUpdateDto(
              userInput as ClinicianInput,
              getUserDtosSelector(getState())[userId]?.user_info.additional_user_info,
            );
      userDto = await updateUser({ userId, updateUserFormDto: { ...updateUserFormDto } });
      if (!userDto) {
        throw new Error();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      markStatus('failed');
      dispatch(
        notify({
          message: `We are having problems updating the ${userLabel(type)} info`,
          severity: 'error',
        }),
      );
      return;
    }
    dispatch(setUsersAction({ users: [userDto] }));
    markStatus('succeeded');
    const name = `${userInput.firstName} ${userInput.lastName}`;

    dispatch(
      notify({
        message: `${capitalized(userLabel(type))} '${name}' was updated successfully`,
        severity: 'success',
      }),
    );
  };
}

// Admin-only thunks. They notify on success and fall through `adminActionErrorMessage`
// for the cross-region 409 case so the user sees a useful message.

export function resetUserPasswordAction(userId: string, displayName: string): AppThunk {
  return async (dispatch) => {
    try {
      await resetUserPassword(userId);
      dispatch(
        notify({
          message: `Password reset email sent to ${displayName}`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        notify({
          message: adminActionErrorMessage(e, 'Failed to send password reset email'),
          severity: 'error',
        }),
      );
    }
  };
}

export function resendUserInviteAction(userId: string, displayName: string): AppThunk {
  return async (dispatch) => {
    try {
      await resendUserInvite(userId);
      dispatch(
        notify({
          message: `Invite re-sent to ${displayName}`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        notify({
          message: adminActionErrorMessage(e, 'Failed to resend invite'),
          severity: 'error',
        }),
      );
    }
  };
}

export function changeUserRoleAction(
  userId: string,
  role: 'admin' | 'clinician',
  displayName: string,
): AppThunk {
  return async (dispatch) => {
    try {
      const userDto = await changeUserRole(userId, role);
      dispatch(setUsersAction({ users: [userDto] }));
      dispatch(
        notify({
          message: `${displayName} is now ${role === 'admin' ? 'an admin' : 'a clinician'}`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        notify({
          message: adminActionErrorMessage(e, 'Failed to change role'),
          severity: 'error',
        }),
      );
    }
  };
}

export function changeUserEmailAction(
  userId: string,
  email: string,
  displayName: string,
): AppThunk {
  return async (dispatch) => {
    try {
      const userDto = await changeUserEmail(userId, email);
      dispatch(setUsersAction({ users: [userDto] }));
      dispatch(
        notify({
          message: `Email for ${displayName} changed to ${email}`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        notify({
          message: adminActionErrorMessage(e, 'Failed to change email'),
          severity: 'error',
        }),
      );
    }
  };
}

export function archiveUserAction(userId: string, displayName: string): AppThunk {
  return async (dispatch) => {
    try {
      const userDto = await archiveUser(userId);
      dispatch(setUsersAction({ users: [userDto] }));
      dispatch(
        notify({
          message: `${displayName} archived`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        notify({
          message: adminActionErrorMessage(e, 'Failed to archive user'),
          severity: 'error',
        }),
      );
    }
  };
}

export function unarchiveUserAction(userId: string, displayName: string): AppThunk {
  return async (dispatch) => {
    try {
      const userDto = await unarchiveUser(userId);
      dispatch(setUsersAction({ users: [userDto] }));
      dispatch(
        notify({
          message: `${displayName} restored`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        notify({
          message: adminActionErrorMessage(e, 'Failed to restore user'),
          severity: 'error',
        }),
      );
    }
  };
}
